import { useState, type ReactNode } from 'react';
import { PlainLink, type LinkComponent } from '@paige/ui';
import './ops.css';

export interface IdChipProps {
  /** The whole id; eight characters are shown, all of it is copied. */
  id: string;
  /** What the id is, for the link's and the button's names: "batch", "document". */
  what: string;
  /** What the link reads as; the first eight characters when absent. */
  label?: string | undefined;
  /** Where the id leads (its timeline); a bare id with a copy control when absent. */
  href?: string | undefined;
  Link?: LinkComponent | undefined;
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false; // no clipboard here (an insecure page, a refused permission): the id is still on screen
  }
}

const SHEETS = (
  <>
    <rect
      x="5"
      y="5"
      width="8"
      height="9"
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M3 11V3.5A1.5 1.5 0 0 1 4.5 2H10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </>
);

const TICK = (
  <path
    d="M3 8.5l3.2 3L13 4.5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  />
);

/** Copies the whole id and says so for a moment. */
function CopyButton({ id, what }: { id: string; what: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    setCopied(await copy(id));
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button
      type="button"
      className="ops-id__copy"
      onClick={() => void onCopy()}
      aria-label={`Copy the ${what} id`}
    >
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
        {copied ? TICK : SHEETS}
      </svg>
      <span className="ops-id__said" role="status">
        {copied ? 'Copied' : ''}
      </span>
    </button>
  );
}

/**
 * An id wherever the Operations screens show one: a link to what it names, and a control that copies all of
 * it. Nobody has to select eight characters of a uuid, and no id is a dead end.
 */
export function IdChip({ id, what, label, href, Link = PlainLink }: IdChipProps): ReactNode {
  const short = id.slice(0, 8);
  return (
    <span className="ops-id" data-id={id}>
      {href ? (
        <Link
          href={href}
          className="ops-id__link"
          aria-label={label ? undefined : `Open the timeline of ${what} ${short}`}
          title={id}
        >
          {label ?? short}
        </Link>
      ) : (
        <code className="ops-id__link" title={id}>
          {label ?? short}
        </code>
      )}
      <CopyButton id={id} what={what} />
    </span>
  );
}
